import { Button, IconButton, ListItemIcon, ListItemText, Menu } from '@mui/material';
import React, { MouseEvent, useState } from 'react';
import { SecuredMenuItem, SECURITY_ACTIONS } from '../auth/Security';
import { BUTTON_VARIANTS, SIZES } from '../constants/constants';
import I18n, { useI18n } from '../i18n/I18n';
import { Icons } from '../icons/Icons';
import { H } from '../Layout';
import { useStyling } from '../Theme';
import ViewTypeSelector from './ViewTypeSelector';

export default function GenerateAndViewButtons({
    setReportVisible,
    views,
    selectedView,
    setSelectedView,
    id,
    generateReportButton,
    reportButtonLabelToken = 'report.stepper.create',
    onOpenViewPanel
}) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const { isMobile } = useStyling();
    const t = useI18n();

    const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const onCreate = () => {
        handleCloseMenu();
        setReportVisible?.(true);
    };

    const onView = () => {
        handleCloseMenu();
        onOpenViewPanel?.();
    };

    return (
        <H gap={1} sx={{ justifyContent: 'end', width: 'auto' }}>
            {generateReportButton && (
                <>
                    {isMobile ? (
                        <IconButton
                            aria-haspopup="true"
                            aria-expanded={open || undefined}
                            onClick={handleOpenMenu}
                            size={SIZES.small}
                            color="primary"
                            aria-label={t(reportButtonLabelToken)}
                        >
                            <generateReportButton.icon />
                        </IconButton>
                    ) : (
                        <Button
                            aria-haspopup="true"
                            aria-expanded={open || undefined}
                            variant={BUTTON_VARIANTS.outlined}
                            onClick={handleOpenMenu}
                            sx={{ textWrap: 'nowrap' }}
                            startIcon={generateReportButton?.icon && <generateReportButton.icon />}
                        >
                            <I18n token={reportButtonLabelToken} />
                        </Button>
                    )}
                    <Menu
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleCloseMenu}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                        slotProps={{
                            list: {
                                sx: {
                                    py: 0
                                }
                            },
                            paper: {
                                sx: {
                                    borderRadius: 1,
                                    boxSizing: 'border-box',
                                    width: anchorEl ? 140 : 'auto'
                                }
                            }
                        }}
                    >
                        <SecuredMenuItem
                            securityAction={SECURITY_ACTIONS.EDIT}
                            onClick={onCreate}
                            divider
                            sx={{ py: 1.5 }}
                        >
                            <ListItemIcon>
                                <Icons.AddOutlined />
                            </ListItemIcon>
                            <ListItemText>
                                <I18n token={'menu.create.title'} />
                            </ListItemText>
                        </SecuredMenuItem>
                        <SecuredMenuItem securityAction={SECURITY_ACTIONS.READ} onClick={onView} sx={{ py: 1.5 }}>
                            <ListItemIcon>
                                <Icons.Visibility />
                            </ListItemIcon>
                            <ListItemText>
                                <I18n token={'common.view'} />
                            </ListItemText>
                        </SecuredMenuItem>
                    </Menu>
                </>
            )}

            {views.length > 1 && (
                <ViewTypeSelector
                    views={views}
                    selectedViewIndex={selectedView}
                    setSelectedViewIndex={setSelectedView}
                    id={id}
                />
            )}
        </H>
    );
}
